import React, { useRef, useState } from 'react';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { uploadImage } from '../services/uploadService';
import { useMyStore } from '../hooks/useMyStore';

interface SingleProps {
  value: string | null;
  onChange: (url: string | null) => void;
  path?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

/**
 * Single-image picker. Click → file picker → uploads → calls onChange(url).
 * Clicking the × clears the value (does not delete from the bucket).
 */
export const ImageUploader: React.FC<SingleProps> = ({
  value,
  onChange,
  path = 'product',
  size = 'md',
  label,
}) => {
  const { store } = useMyStore();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const { url } = await uploadImage(file, store?.clientId, path);
      onChange(url);
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const dimensions =
    size === 'sm'
      ? 'aspect-square h-24'
      : size === 'lg'
        ? 'aspect-[16/9]'
        : 'aspect-square h-40';

  return (
    <div>
      {label && (
        <label className="text-[10px] font-bold uppercase tracking-widest text-text3 mb-1 block">
          {label}
        </label>
      )}

      <div className="relative">
        {value ? (
          <div className={`relative group rounded-xl overflow-hidden bg-gray-100 ${dimensions}`}>
            <img src={value} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(null)}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center transition"
              title="Remove image"
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-2 right-2 h-7 px-3 rounded-full bg-white/95 text-text font-semibold text-[11px] hover:bg-white shadow flex items-center gap-1 disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <Loader2 size={12} className="animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Upload size={12} /> Replace
                </>
              )}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className={`w-full rounded-xl border-2 border-dashed border-border bg-gray-50/80 hover:bg-gray-100 hover:border-primary/40 transition flex flex-col items-center justify-center gap-2 text-text3 ${dimensions} disabled:opacity-60`}
          >
            {uploading ? (
              <>
                <Loader2 size={20} className="animate-spin text-primary" />
                <span className="text-xs font-semibold text-text2">Uploading…</span>
              </>
            ) : (
              <>
                <Upload size={20} />
                <span className="text-xs font-semibold">Click to upload image</span>
                <span className="text-[10px] text-text3">JPG, PNG, WEBP · max 3 MB</span>
              </>
            )}
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
          {error}
        </div>
      )}
    </div>
  );
};

// ── Gallery (multi) uploader ─────────────────────────────────────

interface GalleryProps {
  urls: string[];
  onChange: (next: string[]) => void;
  path?: string;
  maxItems?: number;
}

export const ImageGalleryUploader: React.FC<GalleryProps> = ({
  urls,
  onChange,
  path = 'product-gallery',
  maxItems = 12,
}) => {
  const { store } = useMyStore();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = async (files: FileList) => {
    setError(null);
    setUploading(true);
    const next = [...urls];
    try {
      for (const file of Array.from(files)) {
        if (next.length >= maxItems) break;
        try {
          const { url } = await uploadImage(file, store?.clientId, path);
          if (url && !next.includes(url)) next.push(url);
        } catch (err: any) {
          setError(err?.message || `Failed to upload ${file.name}`);
        }
      }
      onChange(next);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (u: string) => onChange(urls.filter((x) => x !== u));

  return (
    <div className="space-y-2 mt-1">
      <div className="grid grid-cols-4 gap-2">
        {urls.map((u) => (
          <div key={u} className="relative group aspect-square rounded-lg bg-gray-100 overflow-hidden">
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(u)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
            >
              <X size={12} />
            </button>
          </div>
        ))}

        {urls.length < maxItems && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-lg border-2 border-dashed border-border bg-gray-50 hover:bg-gray-100 hover:border-primary/40 transition flex flex-col items-center justify-center gap-1 text-text3 disabled:opacity-60"
          >
            {uploading ? (
              <Loader2 size={18} className="animate-spin text-primary" />
            ) : (
              <>
                <Upload size={16} />
                <span className="text-[10px] font-bold uppercase tracking-widest">Add</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/jpg,image/webp"
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
        }}
      />

      {urls.length === 0 && !uploading && (
        <div className="flex items-center gap-2 text-[11px] text-text3">
          <ImageIcon size={12} />
          <span>JPG, PNG, WEBP · max 3 MB each · up to {maxItems}</span>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
          {error}
        </div>
      )}
    </div>
  );
};
